import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  Card, 
  Col, 
  message,
  DatePicker,
  Drawer,
  Descriptions,
  Spin,
  Divider,
  Tag
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import { useNavigate } from 'react-router-dom';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import * as orderService from '../../../../services/order/orderService';
import { ORDER_STATUS_OPTIONS } from '../../../../constants/order';

const { RangePicker } = DatePicker;

/**
 * 缺货订单列表页
 *路: /order/shortage-order/list
 */
const ShortageOrderList = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
  });

  // 渠道下拉选项（从后端动态获取）
  const [channelOptions, setChannelOptions] = useState([]);

  // Drawer状态管理
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  // 打开详情Drawer
  const handleViewDetail = async (orderNo) => {
    setDrawerVisible(true);
    setDrawerLoading(true);
    try {
      const response = await orderService.getShortageOrderByOrderNo(orderNo);
      if (response.success) {
        setCurrentOrder(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询缺货订单详情失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setDrawerLoading(false);
    }
  };

  // 关闭Drawer
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setCurrentOrder(null);
  };

  //表格列定义
  //策略：仅 orderNo 左固定 + 显式 width（主键，必须左固定方便识别）；
  //其余列不写 width，由 AntD 按"表头文案 + 单元格内容"中最宽者自适应；
  //配合 ellipsis: true 防止极长内容溢出；scroll.x: 'max-content' 让容器不够时自动出横向滚动条，彻底兼容泰语等长文案场景。
  const columns = [
    {
      title: t('order.shortageOrder.list.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      fixed: 'left',
      width: 180,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(record.orderNo)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: t('order.shortageOrder.list.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      ellipsis: true,
      render: (channel) => {
        const option = channelOptions.find(opt => opt.value === channel);
        return option?.label || channel;
      }
    },
    {
      title: t('order.shortageOrder.list.table.orderStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      ellipsis: true,
      render: (status) => {
        const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
        const colorMap = {
          'CREATED': 'blue',
          'IN_PROGRESS': 'processing',
          'COMPLETED': 'green',
          'CANCELLED': 'default',
          'RETURN': 'red'
        };
        return (
          <span style={{
            color: colorMap[status] === 'blue' ? '#1890ff' :
                  colorMap[status] === 'green' ? '#52c41a' :
                  colorMap[status] === 'red' ? '#ff4d4f' : '#8c8c8c',
            fontWeight: '500'
          }}>
            {t(option?.label || status)}
          </span>
        );
      }
    },
    {
      title: t('order.shortageOrder.list.table.buyerName'),
      dataIndex: 'buyerName',
      key: 'buyerName',
      ellipsis: true
    },
    {
      title: t('order.shortageOrder.list.table.skuQty'),
      dataIndex: 'skuQty',
      key: 'skuQty',
      align: 'center',
      ellipsis: true
    },
    {
      title: t('order.shortageOrder.list.table.totalMu'),
      dataIndex: 'totalMu',
      key: 'totalMu',
      align: 'center',
      ellipsis: true
    },
    {
      title: t('order.shortageOrder.list.table.shortageSkuCount'),
      dataIndex: 'shortageSkuCount',
      key: 'shortageSkuCount',
      align: 'center',
      ellipsis: true,
      render: (count) => (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          {count}
        </span>
      )
    },
    {
      title: t('order.shortageOrder.list.table.shortageItemCount'),
      dataIndex: 'shortageItemCount',
      key: 'shortageItemCount',
      align: 'center',
      ellipsis: true,
      render: (count) => (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          {count}
        </span>
      )
    },
    {
      title: t('order.shortageOrder.list.table.replacedSkuCount'),
      dataIndex: 'replacedSkuCount',
      key: 'replacedSkuCount',
      align: 'center',
      ellipsis: true
    },
    {
      title: t('order.shortageOrder.list.table.unreplacedSkuCount'),
      dataIndex: 'unreplacedSkuCount',
      key: 'unreplacedSkuCount',
      align: 'center',
      ellipsis: true,
      render: (count) => (
        <span style={{ color: count > 0 ? '#ff4d4f' : '#52c41a', fontWeight: '500' }}>
          {count}
        </span>
      )
    },
    {
      title: t('order.shortageOrder.list.table.pickerName'),
      dataIndex: 'pickerName',
      key: 'pickerName',
      ellipsis: true
    },
    {
      title: t('order.shortageOrder.list.table.createTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      ellipsis: true,
      render: (createTime) => dayjs(createTime).format('DD-MM-YYYY HH:mm:ss')
    },
    {
      title: t('order.shortageOrder.list.table.actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewDetail(record.orderNo)}
        >
          {t('order.shortageOrder.list.actions.view')}
        </Button>
      )
    }
  ];

  // 构建筛选参数（查询和导出共用）
  const buildFilterParams = () => {
    const values = { ...form.getFieldsValue() };
    // 处理时间范围
    if (values.createTime && values.createTime.length === 2) {
      values.createTimeStart = values.createTime[0].valueOf();
      values.createTimeEnd = values.createTime[1].valueOf();
    }
    delete values.createTime;
    return values;
  };

  // 获取数据
  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        ...buildFilterParams(),
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params
      };

      const response = await orderService.pageShortageOrder(searchParams);

      if (response.success) {
        const data = response.data || {};
        setDataSource(data.tableData || []);
        setPagination(prev => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询缺货订单失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  //初始加载数据 + 渠道选项
  useEffect(() => {
    fetchData();
    orderService.getChannelOptions().then(res => {
      if (res.success && res.data) {
        setChannelOptions(res.data.map(o => ({ value: o.value, label: o.label })));
      }
    });
  }, []);

  //处理搜索
  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 导出（使用与查询完全相同的筛选条件）
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const searchParams = buildFilterParams();
      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await orderService.exportShortageOrder(searchParams);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('common.message.exportSuccess') || 'Export successful');
      } else {
        message.destroy();
        message.error(response.errorMessage || t('common.message.exportFailed') || 'Export failed');
      }
    } catch (error) {
      message.destroy();
      console.error('导出缺货订单失败:', error);
      message.error(t('common.message.exportFailed') || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // 分页变化
  const handleTableChange = (paginationConfig) => {
    const newPagination = {
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };
    setPagination(newPagination);
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 */}
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        title={t('order.shortageOrder.list.title')}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item 
            name="orderNo" 
            label={t('order.shortageOrder.list.search.orderNo')}
          >
            <Input placeholder={t('order.shortageOrder.list.search.placeholder.orderNo')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="channel" 
            label={t('order.shortageOrder.list.search.channel')}
          >
            <Select 
              placeholder={t('order.shortageOrder.list.search.placeholder.channel')}
              options={channelOptions.map(option => ({
                value: option.value,
                label: option.label
              }))}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="pickerName" 
            label={t('order.shortageOrder.list.search.pickerName')}
          >
            <Input placeholder={t('order.shortageOrder.list.search.placeholder.pickerName')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item 
            name="createTime" 
            label={t('order.shortageOrder.list.search.createTime')}
          >
            <RangePicker 
              showTime 
              format="DD-MM-YYYY HH:mm:ss"
              placeholder={[
                t('common.pleaseInput'),
                t('common.pleaseInput')
              ]}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 操作栏 */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
          onClick={handleExport}
          loading={exportLoading}
        >
          Export
        </Button>
      </div>

      {/* 数据表格 */}
      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="orderNo"
            scroll={{ x: 'max-content', y: scrollY }}
          />
        </div>
      </Card>

      {/* 订单详情Drawer */}
      <Drawer
        title={t('order.shortageOrder.detail.title')}
        placement="right"
        width={960}
        open={drawerVisible}
        onClose={handleCloseDrawer}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleCloseDrawer}>
              {t('common.cancel')}
            </Button>
          </div>
        }
      >
        <Spin spinning={drawerLoading}>
          {currentOrder && (
            <>
              {/* 基本信息 */}
              <Descriptions 
                title={t('order.shortageOrder.detail.basicInfo')} 
                bordered 
                column={2}
                size="small"
              >
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.orderNo')}>
                  {currentOrder.orderNo}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.channel')}>
                  {(() => {
                    const option = channelOptions.find(opt => opt.value === currentOrder.channel);
                    return option?.label || currentOrder.channel || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.orderStatus')}>
                  {(() => {
                    const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === currentOrder.orderStatus);
                    return t(option?.label || currentOrder.orderStatus);
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.buyerName')}>
                  {currentOrder.buyerName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.skuQty')}>
                  {currentOrder.skuQty}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.totalMu')}>
                  {currentOrder.totalMu}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.createTime')}>
                  {currentOrder.createTime ? dayjs(currentOrder.createTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 缺货信息 */}
              <Descriptions 
                title={t('order.shortageOrder.detail.shortageInfo')} 
                bordered 
                column={2}
                size="small"
              >
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.shortageSkuCount')}>
                  <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                    {currentOrder.shortageSkuCount || 0}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.shortageItemCount')}>
                  <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                    {currentOrder.shortageItemCount || 0}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.replacedSkuCount')}>
                  {currentOrder.replacedSkuCount || 0}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.unreplacedSkuCount')}>
                  <span style={{ color: (currentOrder.unreplacedSkuCount || 0) > 0 ? '#ff4d4f' : '#52c41a', fontWeight: '500' }}>
                    {currentOrder.unreplacedSkuCount || 0}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.pickerName')}>
                  {currentOrder.pickerName || '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 买家信息 */}
              <Descriptions 
                title={t('order.shortageOrder.detail.buyerInfo')} 
                bordered 
                column={2}
                size="small"
              >
                <Descriptions.Item label={t('order.shortageOrder.detail.basic.buyerName')}>
                  {currentOrder.buyerName || '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 缺货明细 */}
              <div>
                <h4 style={{ marginBottom: 16 }}>{t('order.shortageOrder.detail.shortage.title')}</h4>
                <Table
                  dataSource={currentOrder.shortageDetails || []}
                  rowKey="lineNo"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t('order.shortageOrder.detail.shortage.lineNo'),
                      dataIndex: 'lineNo',
                      width: 80
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.itemCode'),
                      dataIndex: 'itemCode',
                      width: 120
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.itemName'),
                      dataIndex: 'itemName',
                      width: 150
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.planQty'),
                      dataIndex: 'planQty',
                      width: 80,
                      align: 'center'
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.actualPickedQty'),
                      dataIndex: 'actualPickedQty',
                      width: 80,
                      align: 'center'
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.shortageQty'),
                      dataIndex: 'shortageQty',
                      width: 80,
                      align: 'center',
                      render: (val) => (
                        <span style={{ color: (val || 0) > 0 ? '#ff4d4f' : 'inherit', fontWeight: (val || 0) > 0 ? '500' : 'normal' }}>
                          {val || 0}
                        </span>
                      )
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.isReplaced'),
                      dataIndex: 'isReplaced',
                      width: 80,
                      align: 'center',
                      render: (val) => (
                        <Tag color={val ? 'green' : 'default'}>
                          {val ? t('common.yes') : t('common.no')}
                        </Tag>
                      )
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.substituteItemName'),
                      dataIndex: 'substituteItemName',
                      width: 150
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.unitPrice'),
                      dataIndex: 'unitPrice',
                      width: 100,
                      align: 'right',
                      render: (val) => `฿${val?.toFixed(3) || '0.000'}`
                    },
                    {
                      title: t('order.shortageOrder.detail.shortage.shortageAmount'),
                      dataIndex: 'shortageAmount',
                      width: 100,
                      align: 'right',
                      render: (val) => (
                        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                          ฿{val?.toFixed(3) || '0.000'}
                        </span>
                      )
                    }
                  ]}
                />
              </div>
            </>
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default ShortageOrderList;